import React, { useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist";
import { Loader2, MapPin } from "lucide-react";
import api from "@/lib/api";

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

// Small plan preview: click to place a new marker (normalised x/y, page 1 for PDFs).
export default function PlanMarkerPicker({ planId, value, onChange }) {
  const [img, setImg] = useState("");
  const [loading, setLoading] = useState(true);
  const [positions, setPositions] = useState([]);
  const canvasRef = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    let alive = true;
    let url = "";
    setLoading(true); setImg("");
    (async () => {
      try {
        const [{ data: plan }, blob, pos] = await Promise.all([
          api.get(`/plans/${planId}`), api.get(`/plans/${planId}/file`, { responseType: "blob" }), api.get(`/plans/${planId}/positions`),
        ]);
        if (!alive) return;
        setPositions(pos.data || []);
        if ((plan.content_type || "").includes("pdf")) {
          const doc = await pdfjsLib.getDocument({ data: await blob.data.arrayBuffer() }).promise;
          const page = await doc.getPage(1);
          const vp = page.getViewport({ scale: Math.min(3, 1400 / page.getViewport({ scale: 1 }).width) });
          const c = canvasRef.current;
          if (!c || !alive) return;
          c.width = vp.width; c.height = vp.height;
          await page.render({ canvasContext: c.getContext("2d"), viewport: vp }).promise;
        } else {
          url = URL.createObjectURL(blob.data); setImg(url);
        }
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [planId]);

  const place = (e) => {
    const r = boxRef.current.getBoundingClientRect();
    onChange({ type: "point", x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)), y: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)) });
  };
  const anchor = (m) => (m?.x != null ? m : m?.points?.[0] || { x: m?.x1 ?? 0, y: m?.y1 ?? 0 });

  return (
    <div className="space-y-1" data-testid="plan-marker-picker">
      <p className="text-xs text-slate-500">Tippen Sie auf die Stelle im Plan, an der der Marker gesetzt werden soll.</p>
      <div className="relative border border-slate-200 rounded-lg overflow-auto max-h-[45vh] bg-slate-50">
        {loading && <div className="p-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>}
        <div ref={boxRef} className="relative cursor-crosshair" onClick={place} data-testid="plan-marker-area">
          {img ? <img src={img} alt="Bauplan" className="block w-full select-none" draggable={false} /> : <canvas ref={canvasRef} className="block w-full" />}
          {positions.map((p) => { const a = anchor(p.marker); return <span key={p.id} className="absolute h-2 w-2 rounded-full bg-slate-400 -translate-x-1/2 -translate-y-1/2" style={{ left: `${a.x * 100}%`, top: `${a.y * 100}%` }} title={p.number} />; })}
          {value && <MapPin className="absolute h-7 w-7 text-primary fill-primary/30 -translate-x-1/2 -translate-y-full drop-shadow" style={{ left: `${value.x * 100}%`, top: `${value.y * 100}%` }} data-testid="plan-marker-pin" />}
        </div>
      </div>
      {value && <p className="text-xs text-emerald-700" data-testid="plan-marker-set">Marker gesetzt – beim Speichern wird eine neue Position angelegt.</p>}
    </div>
  );
}
